/**
 * Plan restriction constants and helper utilities
 */

export const PLAN_TYPES = {
  FREE: 'Free',
  BASIC: 'Basic',
  POPULAR: 'Popular',
  PRO: 'Pro'
};

export const FEATURE_NAMES = {
  VIEW_OTHERS_PORTFOLIO: 'view_others_portfolio',
  JOB_PROFILES: 'job_profiles',
  CART: 'cart',
  COMMENT_OPTION: 'comment_option',
  APTITUDE: 'aptitude',
  SCORECARD: 'scorecard',
  EXAMS: 'exams',
  EXAMINATIONS: 'examinations',
  NOTES: 'notes',
  COURSES_PREPARATION: 'courses_preparation',
  EXPLORE_OPPORTUNITIES: 'explore_opportunities',
  HARD_SKILLS_PROFESSIONAL: 'hard_skills_professional',
  INDUSTRY_OUTLOOK: 'industry_outlook',
  SWITCH_FEATURE: 'switch_feature'
};

export const PROFILE_TYPES = {
  STUDENT: 'student',
  FRESHER: 'fresher',
  PROFESSIONAL: 'professional'
};

export const RESTRICTION_TYPES = {
  CAREER_MAP: 'career_map',
  PORTFOLIO: 'portfolio',
  PROFESSIONAL_PROFILE: 'professional_profile',
  FRESHER_PROFILE: 'fresher_profile',
  MENTOR_CREDITS: 'mentor_credits',
  JOB_PROFILES: 'job_profiles',
  APTITUDE: 'aptitude',
  SCORECARD: 'scorecard',
  EXAMINATIONS: 'examinations',
  INDUSTRY_OUTLOOK: 'industry_outlook',
  HARD_SKILLS: 'hard_skills',
  EXPLORE_OPPORTUNITIES: 'explore_opportunities',
  CART: 'cart',
  NOTES: 'notes',
  COURSES_PREPARATION: 'courses_preparation'
};

/**
 * Check if a plan allows unlimited usage of a feature
 */
export const isUnlimited = (limit) => {
  return limit === -1 || limit === null || limit === undefined;
};

/**
 * Format remaining count for display
 */
export const formatRemainingCount = (remaining, total) => {
  if (isUnlimited(total)) {
    return 'Unlimited';
  }
  return `${remaining} of ${total} remaining`;
};

/**
 * Calculate usage percentage
 */
export const calculateUsagePercentage = (used, total) => {
  if (isUnlimited(total)) {
    return 0;
  }
  if (total === 0) {
    return 100;
  }
  return Math.min((used / total) * 100, 100);
};

/**
 * Check if usage is near limit (>80%)
 */
export const isNearLimit = (used, total) => {
  if (isUnlimited(total)) {
    return false;
  }
  return calculateUsagePercentage(used, total) > 80;
};

/**
 * Get plan tier level (for comparison)
 */
export const getPlanTier = (planName) => {
  const tiers = {
    [PLAN_TYPES.FREE]: 0,
    [PLAN_TYPES.BASIC]: 1,
    [PLAN_TYPES.POPULAR]: 2,
    [PLAN_TYPES.PRO]: 3
  };
  return tiers[planName] || 0;
};

/**
 * Check if upgrade is needed to access feature
 */
export const needsUpgrade = (currentPlan, requiredPlan) => {
  return getPlanTier(currentPlan) < getPlanTier(requiredPlan);
};

/**
 * Get recommended plan for a feature
 */
export const getRecommendedPlan = (featureName) => {
  const featurePlanMap = {
    [FEATURE_NAMES.APTITUDE]: PLAN_TYPES.BASIC,
    [FEATURE_NAMES.EXAMINATIONS]: PLAN_TYPES.BASIC,
    [FEATURE_NAMES.COURSES_PREPARATION]: PLAN_TYPES.BASIC,
    [FEATURE_NAMES.JOB_PROFILES]: PLAN_TYPES.POPULAR,
    [FEATURE_NAMES.SCORECARD]: PLAN_TYPES.PRO,
    [FEATURE_NAMES.HARD_SKILLS_PROFESSIONAL]: PLAN_TYPES.PRO,
    [FEATURE_NAMES.INDUSTRY_OUTLOOK]: PLAN_TYPES.PRO,
    [FEATURE_NAMES.EXPLORE_OPPORTUNITIES]: PLAN_TYPES.PRO,
    [RESTRICTION_TYPES.PROFESSIONAL_PROFILE]: PLAN_TYPES.PRO
  };
  
  return featurePlanMap[featureName] || PLAN_TYPES.BASIC;
};

/**
 * Allowed exam categories for Free plan
 */
export const FREE_PLAN_EXAM_CATEGORIES = [
  'Architecture',
  'Engineering',
  'General UG Programs',
  'Hospitality'
];

/**
 * Filter exam categories based on plan
 */
export const filterExamCategoriesByPlan = (examCategories, planName) => {
  if (!planName || planName !== PLAN_TYPES.FREE) {
    return examCategories;
  }
  
  return examCategories.filter(cat => 
    FREE_PLAN_EXAM_CATEGORIES.includes(cat.category)
  );
};

/**
 * Format plan price for display
 */
export const formatPlanPrice = (price) => {
  if (price === 0 || price === '0') {
    return 'Free';
  }
  return `₹${parseFloat(price).toLocaleString('en-IN')}`;
};

/**
 * Get plan color scheme
 */
export const getPlanColor = (planName) => {
  const colors = {
    [PLAN_TYPES.FREE]: {
      bg: 'bg-gray-100',
      text: 'text-gray-700',
      border: 'border-gray-300',
      gradient: 'from-gray-400 to-gray-500'
    },
    [PLAN_TYPES.BASIC]: {
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      border: 'border-blue-300',
      gradient: 'from-blue-400 to-blue-500'
    },
    [PLAN_TYPES.POPULAR]: {
      bg: 'bg-amber-100',
      text: 'text-amber-700',
      border: 'border-amber-300',
      gradient: 'from-amber-400 to-orange-500'
    },
    [PLAN_TYPES.PRO]: {
      bg: 'bg-purple-100',
      text: 'text-purple-700',
      border: 'border-purple-300',
      gradient: 'from-purple-400 to-purple-600'
    }
  };
  
  return colors[planName] || colors[PLAN_TYPES.FREE];
};

export default {
  PLAN_TYPES,
  FEATURE_NAMES,
  PROFILE_TYPES,
  RESTRICTION_TYPES,
  isUnlimited,
  formatRemainingCount,
  calculateUsagePercentage,
  isNearLimit,
  getPlanTier,
  needsUpgrade,
  getRecommendedPlan,
  formatPlanPrice,
  getPlanColor
};
